import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSites } from "@/contexts/SiteContext";
import PageHeader from "@/components/PageHeader";
import { Search, AlertTriangle, Info, AlertCircle, RefreshCcw, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FixButton, FixDrawer, FIXABLE_CATEGORIES } from "@/components/SeoFixDrawer";
import { IssueEvidence, ConfidenceBadge } from "@/components/IssueEvidence";
import { ScoreOverview, PriorityBadge, IssueScoring, FiltersBar, sortByPriorityFirst } from "@/components/ScorePanel";
import { AltDiagnosticDrawer } from "@/components/AltDiagnosticDrawer";
import { BulkFixWizard } from "@/components/BulkFixWizard";
import { FixHistoryPanel } from "@/components/FixHistoryPanel";
import { VeloBridgePanel } from "@/components/VeloBridgePanel";
import { EmergentBridgePanel } from "@/components/EmergentBridgePanel";
import { HreflangGuidePanel } from "@/components/HreflangGuidePanel";
import { LanguageScoreHistory } from "@/components/LanguageScoreHistory";
import { Sparkles } from "lucide-react";

const sevConf = {
  high: { color: "#DC2626", bg: "#FEE2E2", icon: AlertCircle, label: "Critique" },
  medium: { color: "#D97706", bg: "#FEF3C7", icon: AlertTriangle, label: "Important" },
  low: { color: "#0EA5E9", bg: "#E0F2FE", icon: Info, label: "Mineur" },
};

const LANG_CODES = ["fr", "de", "en", "it", "es", "pt", "nl"];
const pageLangOf = (url) => {
  const u = (url || "").toLowerCase();
  const sub = /^https?:\/\/([a-z]{2})\./.exec(u);
  if (sub && LANG_CODES.includes(sub[1])) return sub[1];
  const m = /^https?:\/\/[^/]+\/([a-z]{2})(?:[/?#]|$)/.exec(u);
  return m && LANG_CODES.includes(m[1]) ? m[1] : "fr";
};
const issueLang = (i) => i.page_lang || pageLangOf(i.page_url);

function NoSite() {
  return (
    <div className="border border-dashed border-slate-300 bg-white rounded-md p-10 text-center">
      <p className="text-sm text-slate-600">Sélectionnez ou connectez un site Wix pour lancer un audit.</p>
    </div>
  );
}

export default function Audit() {
  const { activeSite } = useSites();
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [fixIssue, setFixIssue] = useState(null);
  const [altIssue, setAltIssue] = useState(null);
  const [hreflangIssue, setHreflangIssue] = useState(null);
  const [bulkIssues, setBulkIssues] = useState(null);
  const [dupScan, setDupScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fixedKeys, setFixedKeys] = useState(new Set());
  const [resolvedMap, setResolvedMap] = useState({});
  const [filters, setFilters] = useState({ priority: null, confidence: null, quickWins: false, category: null, lang: null });
  const [sortOn, setSortOn] = useState(true);

  const loadFixedKeys = async (siteId) => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/seo-fix/resolved-keys/${siteId}`);
      const map = {};
      (data.resolved || []).forEach((r) => { map[r.key] = r; });
      setResolvedMap(map);
      setFixedKeys(new Set(Object.keys(map)));
    } catch { /* noop */ }
  };

  const allIssues = report ? report.issues : [];
  const visibleIssues = allIssues.filter((i) => !fixedKeys.has(`${i.page_url}|${i.category}`));
  const hiddenFixedCount = allIssues.length - visibleIssues.length;
  const hiddenViaBridge = allIssues.filter((i) => resolvedMap[`${i.page_url}|${i.category}`]?.source === "emergent_bridge").length;
  const hiddenPending = allIssues.filter((i) => resolvedMap[`${i.page_url}|${i.category}`]?.state === "APPLIED_PENDING_VERIFICATION").length;
  const hasScoring = Boolean(report?.scoring);
  let displayIssues = visibleIssues.filter((i) => {
    const s = i.scoring;
    if (filters.priority && s?.priority !== filters.priority) return false;
    if (filters.confidence && (s?.confidence || "UNKNOWN") !== filters.confidence) return false;
    if (filters.quickWins && !s?.quick_win) return false;
    if (filters.category && s?.score_category !== filters.category) return false;
    if (filters.lang && issueLang(i) !== filters.lang) return false;
    return true;
  });
  const langCounts = {};
  visibleIssues.forEach((i) => { const l = issueLang(i); langCounts[l] = (langCounts[l] || 0) + 1; });
  const langsPresent = Object.entries(langCounts).sort((a, b) => b[1] - a[1]);
  if (sortOn && hasScoring) displayIssues = sortByPriorityFirst(displayIssues);
  const fixableIssues = visibleIssues.filter((i) => !i.type && FIXABLE_CATEGORIES.includes(i.category) && i.priority !== "C");
  const cExcludedCount = visibleIssues.filter((i) => !i.type && FIXABLE_CATEGORIES.includes(i.category) && i.priority === "C").length;
  const pageFixCount = {};
  fixableIssues.forEach((i) => { pageFixCount[i.page_url] = (pageFixCount[i.page_url] || 0) + 1; });
  const firstRowForPage = {};
  displayIssues.forEach((iss, i) => {
    if (!(iss.page_url in firstRowForPage)) firstRowForPage[iss.page_url] = i;
  });

  const loadHistory = async (siteId) => {
    if (!siteId) return;
    const { data } = await api.get(`/sites/${siteId}/audits`);
    setHistory(data);
    if (data.length) setReport((prev) => prev ?? data[0]);
  };

  useEffect(() => {
    setReport(null);
    setHistory([]);
    setFixedKeys(new Set());
    setResolvedMap({});
    if (activeSite) {
      loadHistory(activeSite.id);
      loadFixedKeys(activeSite.id);
    }
     
  }, [activeSite?.id]);

  const runAudit = async () => {
    if (!activeSite) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/sites/${activeSite.id}/audit`);
      setReport(data);
      toast.success(`Audit terminé · score ${data.score}/100`);
      loadHistory(activeSite.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'audit");
    } finally {
      setRunning(false);
    }
  };

  const runDuplicateScan = async () => {
    if (!activeSite) return;
    setScanning(true);
    setDupScan(null);
    try {
      const { data } = await api.post(`/sites/${activeSite.id}/duplicate-scan`);
      setDupScan(data);
      const totalIssues = (data.pairs?.length || 0) + (data.duplicate_titles?.length || 0) + (data.duplicate_metas?.length || 0);
      if (totalIssues === 0) {
        toast.success("Aucun doublon détecté — contenu unique");
      } else {
        toast.warning(`${totalIssues} problème(s) de duplication détecté(s)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du scan");
    } finally {
      setScanning(false);
    }
  };

  if (!activeSite) {
    return (
      <div className="p-6 md:p-8 max-w-7xl">
        <PageHeader overline="Audit SEO" title="Analyse technique de votre site" />
        <NoSite />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <PageHeader
        overline={`Audit · ${activeSite.label}`}
        title="Audit SEO automatique"
        description="Analyse de toutes les pages publiques du site : titres, méta, H1/H2, alt images, canonical, Open Graph, Schema, contenu, URLs — plus l'indexation avancée (redirections, soft 404, hreflang, sitemap, indexabilité — diagnostic avec preuves)."
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={runDuplicateScan}
              disabled={scanning}
              data-testid="run-duplicate-scan-button"
              className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60 text-slate-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              {scanning ? "Scan…" : "Détecter doublons"}
            </button>
            <button
              onClick={runAudit}
              disabled={running}
              data-testid="run-audit-button"
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#001D6B] disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              {running ? "Audit en cours…" : "Lancer un audit"}
            </button>
          </div>
        }
      />

      {dupScan && (
        <div className="mb-6 border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="duplicate-scan-results">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="overline">Détection de doublons — {dupScan.pages_scanned} pages analysées</div>
            <button onClick={() => setDupScan(null)} className="text-slate-400 hover:text-slate-700 text-xs">Fermer</button>
          </div>
          {(dupScan.recommendations || []).length > 0 && (
            <div className="p-4 border-b border-slate-100 bg-amber-50/40">
              {dupScan.recommendations.map((r, i) => (
                <div key={i} className="text-sm text-slate-800 flex items-start gap-2 py-1">
                  <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" /> {r}
                </div>
              ))}
            </div>
          )}
          {dupScan.pairs?.length > 0 && (
            <div className="p-4 border-b border-slate-100">
              <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Pages similaires (cannibalisation potentielle)</div>
              <div className="space-y-2">
                {dupScan.pairs.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md" data-testid={`duplicate-pair-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate" title={p.page_a.title}>{p.page_a.title || "(sans titre)"}</div>
                      {p.page_a.url && <div className="text-xs text-slate-500 truncate font-mono">{p.page_a.url}</div>}
                    </div>
                    <div className="text-slate-300">↔</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate" title={p.page_b.title}>{p.page_b.title || "(sans titre)"}</div>
                      {p.page_b.url && <div className="text-xs text-slate-500 truncate font-mono">{p.page_b.url}</div>}
                    </div>
                    <div className={`font-mono text-sm px-2 py-1 rounded ${p.severity === "high" ? "bg-red-100 text-red-700" : p.severity === "medium" ? "bg-amber-100 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                      {Math.round(p.similarity * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dupScan.duplicate_titles?.length > 0 && (
            <div className="p-4 border-b border-slate-100">
              <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Titres H1 identiques</div>
              <div className="space-y-2">
                {dupScan.duplicate_titles.map((group, i) => (
                  <div key={i} className="p-3 border border-slate-200 rounded-md">
                    <div className="text-sm font-medium text-slate-900 mb-1">&quot;{group[0].title}&quot;</div>
                    <ul className="text-xs text-slate-600 font-mono space-y-0.5">
                      {group.map((p) => <li key={p.id}>{p.url}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dupScan.duplicate_metas?.length > 0 && (
            <div className="p-4">
              <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Meta descriptions identiques</div>
              <div className="space-y-2">
                {dupScan.duplicate_metas.map((group, i) => (
                  <div key={i} className="p-3 border border-slate-200 rounded-md">
                    <div className="text-sm text-slate-700 mb-1 italic">&quot;{group[0].meta}&quot;</div>
                    <ul className="text-xs text-slate-600 font-mono space-y-0.5">
                      {group.map((p) => <li key={p.id}>{p.url || p.title}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!report ? (
        <>
          <div className="border border-dashed border-slate-300 bg-white rounded-md p-10 text-center" data-testid="audit-empty">
            <Search className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Lancez votre premier audit pour {activeSite.name}.</p>
          </div>
          {activeSite?.site_type === "wix" && <VeloBridgePanel siteId={activeSite.id} />}
          {activeSite && <EmergentBridgePanel siteId={activeSite.id} />}
        </>
      ) : (
        <>
          <ScoreOverview report={report} selectedCategory={filters.category}
            onSelectCategory={(c) => setFilters((f) => ({ ...f, category: c }))} />
          {report.scoring?.by_language ? (
            <div className="mb-5 border border-slate-200 bg-white rounded-md p-4" data-testid="language-scores">
              <div className="overline mb-3">Score SEO par langue</div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(report.scoring.by_language)
                  .sort((a, b) => (a[1].score ?? 101) - (b[1].score ?? 101))
                  .map(([code, ls], idx, arr) => (
                    <button key={code}
                      onClick={() => setFilters((f) => ({ ...f, lang: f.lang === code ? null : code }))}
                      data-testid={`language-score-${code}`}
                      title="Cliquer pour filtrer les problèmes de cette langue"
                      className={`text-left border rounded-md px-4 py-3 min-w-[130px] transition-colors ${filters.lang === code ? "border-[#002FA7] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-slate-700 uppercase">{code}</span>
                        {idx === 0 && arr.length > 1 && ls.score !== null && arr[1]?.[1]?.score !== ls.score && (
                          <span data-testid="language-score-worst" className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">En retard</span>
                        )}
                      </div>
                      <div className="font-mono text-2xl font-semibold" style={{ color: ls.score === null ? "#94A3B8" : ls.score >= 75 ? "#16A34A" : ls.score >= 50 ? "#D97706" : "#DC2626" }}>
                        {ls.score === null ? "—" : ls.score}<span className="text-sm text-slate-400">/100</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{ls.pages} page(s) · {ls.issues} problème(s)</div>
                    </button>
                  ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-400">Cliquez une langue pour filtrer les problèmes détectés.</div>
              <LanguageScoreHistory history={history} />
            </div>
          ) : hasScoring && langsPresent.length > 1 ? (
            <div className="mb-5 border border-slate-200 bg-slate-50 rounded-md px-4 py-2.5 text-xs text-slate-600" data-testid="language-scores-hint">
              Site multilingue détecté — relancez un audit pour obtenir le score SEO par langue.
            </div>
          ) : null}
          {!hasScoring && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="border border-slate-200 bg-white rounded-md p-5" data-testid="audit-score-legacy">
              <div className="overline mb-3">Score SEO</div>
              <div className={`font-mono text-4xl font-semibold ${report.score >= 75 ? "text-[#16A34A]" : report.score >= 50 ? "text-[#D97706]" : "text-[#DC2626]"}`}>
                {report.score}<span className="text-xl text-slate-400">/100</span>
              </div>
              <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${report.score}%`,
                    background: report.score >= 75 ? "#16A34A" : report.score >= 50 ? "#D97706" : "#DC2626",
                  }}
                />
              </div>
            </div>
            <div className="border border-slate-200 bg-white rounded-md p-5">
              <div className="overline mb-3">Pages analysées</div>
              <div className="font-mono text-4xl font-semibold text-slate-950">{report.total_pages}</div>
            </div>
            <div className="border border-slate-200 bg-white rounded-md p-5">
              <div className="overline mb-3">Critiques</div>
              <div className="font-mono text-4xl font-semibold text-[#DC2626]">{report.summary.high}</div>
            </div>
            <div className="border border-slate-200 bg-white rounded-md p-5">
              <div className="overline mb-3">Importants / mineurs</div>
              <div className="font-mono text-4xl font-semibold text-[#D97706]">
                {report.summary.medium}
                <span className="text-xl text-slate-400"> / {report.summary.low}</span>
              </div>
            </div>
          </div>
          )}

          <Tabs defaultValue="issues">
            <TabsList className="bg-transparent border-b border-slate-200 rounded-none p-0 h-auto w-full justify-start gap-6">
              <TabsTrigger
                value="issues"
                data-testid="issues-tab"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-slate-950 px-0 py-3 text-sm"
              >
                Problèmes détectés ({displayIssues.length}{displayIssues.length !== visibleIssues.length ? ` / ${visibleIssues.length}` : ""})
              </TabsTrigger>
              <TabsTrigger
                value="history"
                data-testid="history-tab"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-slate-950 px-0 py-3 text-sm"
              >
                Historique ({history.length})
              </TabsTrigger>
              <TabsTrigger
                value="fixes"
                data-testid="fixes-tab"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-slate-950 px-0 py-3 text-sm"
              >
                Corrections
              </TabsTrigger>
            </TabsList>

            <TabsContent value="issues" className="mt-5">
              {hiddenFixedCount > 0 && (
                <div className="mb-3 flex items-center gap-2 border border-green-200 bg-green-50 rounded-md px-4 py-2 text-sm text-green-800" data-testid="fixed-hidden-note">
                  ✓ {hiddenFixedCount} problème(s) corrigé(s) masqué(s)
                  {hiddenViaBridge > 0 ? ` · dont ${hiddenViaBridge} via Pont Emergent` : ""}
                  {hiddenPending > 0 ? ` · ${hiddenPending} en attente de vérification` : ""}
                  {" "}— détail dans l'onglet Corrections (retour automatique en cas de rollback ou d'override retiré)
                </div>
              )}
              {fixableIssues.length > 0 && (
                <div className="mb-3 flex items-center justify-between border border-[#002FA7]/20 bg-blue-50/50 rounded-md px-4 py-2.5" data-testid="bulk-fix-bar">
                  <span className="text-sm text-slate-700">
                    <strong>{fixableIssues.length}</strong> problème(s) corrigeable(s) automatiquement par l'IA
                    {cExcludedCount > 0 && <span className="text-xs text-slate-500"> · {cExcludedCount} priorité C exclue(s) — manuel/sensible</span>}
                  </span>
                  <button
                    onClick={() => setBulkIssues(fixableIssues.slice(0, 15))}
                    data-testid="bulk-fix-button"
                    className="inline-flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#001D6B] text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Corriger en masse (IA)
                  </button>
                </div>
              )}
              {langsPresent.length > 1 && (
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="lang-filter-bar">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Langue</span>
                  <button onClick={() => setFilters((f) => ({ ...f, lang: null }))} data-testid="lang-filter-all"
                    className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${!filters.lang ? "border-[#002FA7] bg-blue-50 text-[#002FA7] font-medium" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                    Toutes
                  </button>
                  {langsPresent.map(([code, n]) => (
                    <button key={code}
                      onClick={() => setFilters((f) => ({ ...f, lang: f.lang === code ? null : code }))}
                      data-testid={`lang-filter-${code}`}
                      className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${filters.lang === code ? "border-[#002FA7] bg-blue-50 text-[#002FA7] font-medium" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                      {code.toUpperCase()} ({n})
                    </button>
                  ))}
                </div>
              )}
              <FiltersBar filters={filters} setFilters={setFilters} sortOn={sortOn} setSortOn={setSortOn} hasScoring={hasScoring} />
              <div className="border border-slate-200 bg-white rounded-md overflow-hidden" data-testid="audit-issues-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sévérité</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Page</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Catégorie</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Problème / Recommandation</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayIssues.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">
                          {visibleIssues.length > 0 ? "Aucun problème ne correspond aux filtres actifs." : hiddenFixedCount > 0 ? `Aucun problème restant — ${hiddenFixedCount} corrigé(s) (voir l'onglet Corrections).` : "Aucun problème détecté."}
                        </td>
                      </tr>
                    ) : (
                      displayIssues.map((iss, i) => {
                        const sv = sevConf[iss.severity];
                        const Ico = sv.icon;
                        return (
                          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-issue-${i}`}>
                            <td className="px-4 py-3 align-top">
                              <div className="flex items-center gap-1.5">
                                {iss.scoring ? (
                                  <PriorityBadge scoring={iss.scoring} />
                                ) : iss.priority && (
                                  <span data-testid="issue-priority-badge"
                                    title={iss.priority === "A" ? "Impact élevé / correction sûre" : iss.priority === "B" ? "Optimisation utile" : "Manuel / sensible — jamais en masse"}
                                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${iss.priority === "A" ? "bg-red-100 text-red-700" : iss.priority === "B" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>
                                    {iss.priority}
                                  </span>
                                )}
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ background: sv.bg, color: sv.color }}
                                >
                                  <Ico className="w-3 h-3" />
                                  {sv.label}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <div className="font-medium text-slate-900 text-sm">{iss.page_title}</div>
                              <div className="text-xs text-slate-500 truncate max-w-[200px]" title={iss.page_url}>{iss.page_url}</div>
                              {firstRowForPage[iss.page_url] === i && pageFixCount[iss.page_url] >= 2 && (
                                <button
                                  onClick={() => setBulkIssues(fixableIssues.filter((f) => f.page_url === iss.page_url))}
                                  data-testid="fix-page-button"
                                  className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-2 py-0.5 hover:bg-blue-50 transition-colors"
                                >
                                  <Sparkles className="w-3 h-3" /> Corriger cette page ({pageFixCount[iss.page_url]})
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 align-top text-slate-700 text-sm">{iss.category}</td>
                            <td className="px-4 py-3 align-top">
                              <div className="text-slate-900 text-sm font-medium">
                                {iss.message}
                                {iss.confidence && <ConfidenceBadge confidence={iss.confidence} />}
                              </div>
                              <div className="text-xs text-slate-600 mt-1">{iss.recommendation}</div>
                              <IssueScoring scoring={iss.scoring} idx={i} />
                              <IssueEvidence issue={iss} idx={i} />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <FixButton issue={iss} siteId={activeSite?.id} onOpen={setFixIssue} onAltOpen={setAltIssue} onHreflangOpen={setHreflangIssue} />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="fixes" className="mt-5">
              <FixHistoryPanel siteId={activeSite?.id} />
              {activeSite?.site_type === "wix" && <VeloBridgePanel siteId={activeSite.id} />}
              {activeSite && <EmergentBridgePanel siteId={activeSite.id} />}
            </TabsContent>

            <TabsContent value="history" className="mt-5">
              <div className="border border-slate-200 bg-white rounded-md">
                {history.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Aucun audit antérieur.</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {history.map((a) => (
                      <li
                        key={a.id}
                        onClick={() => setReport(a)}
                        className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        data-testid={`audit-history-${a.id}`}
                      >
                        <div>
                          <div className="text-sm font-medium text-slate-900">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                          <div className="text-xs text-slate-500">{a.total_pages} pages · {a.issues.length} problèmes</div>
                        </div>
                        <div className={`font-mono text-lg font-semibold ${a.score >= 75 ? "text-[#16A34A]" : a.score >= 50 ? "text-[#D97706]" : "text-[#DC2626]"}`}>
                          {a.score}/100
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>
          </Tabs>
      {fixIssue && (
        <FixDrawer key={fixIssue.page_url + fixIssue.category} issue={fixIssue} siteId={activeSite?.id}
          onClose={() => { setFixIssue(null); loadFixedKeys(activeSite?.id); }} />
      )}
      {altIssue && (
        <AltDiagnosticDrawer key={altIssue.page_url} issue={altIssue} siteId={activeSite?.id}
          onClose={() => setAltIssue(null)} />
      )}
      {hreflangIssue && (
        <HreflangGuidePanel key={hreflangIssue.page_url + (hreflangIssue.type || "")} issue={hreflangIssue}
          siteId={activeSite?.id} onClose={() => setHreflangIssue(null)} />
      )}
      {bulkIssues && (
        <BulkFixWizard siteId={activeSite?.id} issues={bulkIssues} excludedC={cExcludedCount}
          onClose={() => { setBulkIssues(null); loadFixedKeys(activeSite?.id); }} />
      )}
        </>
      )}
    </div>
  );
}
